import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Card, ListGroup, Badge } from 'react-bootstrap';
import { vacanciesAPI } from '../services/api';

const VacancyForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    requirements: '',
    contact_email: '',
    contact_phone: '',
    application_address: '',
    closing_date: ''
  });
  const [files, setFiles] = useState<FileList | null>(null);
  const [existingDocs, setExistingDocs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      loadVacancy(); 
    }
  }, [id]);

  const loadVacancy = async () => {
    try {
      const response = await vacanciesAPI.getById(String(id));
      const vacancy = response.data;
      setFormData({
        title: vacancy.title,
        description: vacancy.description || '',
        requirements: vacancy.requirements || '',
        contact_email: vacancy.contact_email || '',
        contact_phone: vacancy.contact_phone || '',
        application_address: vacancy.application_address || '',
        closing_date: vacancy.closing_date ? vacancy.closing_date.split('T')[0] : ''
      });
      setExistingDocs(vacancy.documents || []);
    } catch (err) {
      setError('Failed to load vacancy');
    }
  };

  const handleChange = (e: React.ChangeEvent<any>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        data.append(key, value);
      });

      if (files) {
        Array.from(files).forEach(file => {
          data.append('documents', file);
        });
      }

      if (isEdit) {
        await vacanciesAPI.update(String(id), data);
      } else {
        await vacanciesAPI.create(data);
      }

      navigate('/vacancies');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save vacancy');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (docId: number) => {
    if (window.confirm('Delete this document?')) {
      try {
        await vacanciesAPI.deleteDocument(String(docId));
        setExistingDocs(existingDocs.filter(doc => doc.id !== docId));
      } catch (err) {
        setError('Failed to delete document');
      }
    }
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '3rem' }}>
      <Container style={{ maxWidth: '900px' }}>
        <div className="mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            marginBottom: '8px'
          }}>
            {isEdit ? 'Edit Vacancy' : 'Create New Vacancy'}
          </h1>
          <p style={{ color: '#8c8c8c', fontSize: '14px', margin: 0 }}>
            {isEdit ? 'Update vacancy information and documents' : 'Fill in the details to post a new job vacancy'}
          </p>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '24px'
            }}
          >
            {error}
          </Alert>
        )}

        <Card style={{ 
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '24px'
        }}>
          <Card.Body style={{ padding: '32px' }}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>
                  Job Title <span style={{ color: '#ff4d4f' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                  placeholder="Enter job title or position"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Job Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Provide a detailed job description, responsibilities, and duties"
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Requirements</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="requirements"
                  value={formData.requirements}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="List qualifications, skills, and experience required"
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Include education, experience, and skill requirements
                </Form.Text>
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Contact Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="hr@example.com"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-4">
                    <Form.Label style={labelStyle}>Contact Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      style={inputStyle}
                      placeholder="+264 XX XXX XXXX"
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Application Address</Form.Label>
                <Form.Control
                  type="text"
                  name="application_address"
                  value={formData.application_address}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Physical address or email for applications"
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Where applicants should submit their applications
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Closing Date</Form.Label>
                <Form.Control
                  type="date"
                  name="closing_date"
                  value={formData.closing_date}
                  onChange={handleChange}
                  style={inputStyle}
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Last date to accept applications
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label style={labelStyle}>Upload Documents</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={(e: any) => setFiles(e.target.files)}
                  style={inputStyle}
                />
                <Form.Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                  Upload job description documents, application forms, or other relevant files
                </Form.Text>
              </Form.Group>

              {isEdit && existingDocs.length > 0 && (
                <div className="mb-4">
                  <Form.Label style={labelStyle}>Existing Documents</Form.Label>
                  <ListGroup style={{ borderRadius: '6px', overflow: 'hidden' }}>
                    {existingDocs.map(doc => (
                      <ListGroup.Item 
                        key={doc.id} 
                        className="d-flex justify-content-between align-items-center"
                        style={{
                          border: '1px solid #f0f0f0',
                          padding: '12px 16px',
                          backgroundColor: 'white'
                        }}
                      >
                        <div>
                          <span style={{ color: '#262626', fontSize: '14px' }}>{doc.file_name}</span>
                          {doc.file_type && (
                            <Badge 
                              bg="secondary" 
                              className="ms-2"
                              style={{
                                backgroundColor: '#f0f0f0',
                                color: '#595959',
                                fontWeight: 500,
                                padding: '4px 8px',
                                borderRadius: '4px'
                              }}
                            >
                              {doc.file_type}
                            </Badge>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          onClick={() => handleDeleteDocument(doc.id)}
                          style={{
                            backgroundColor: 'white',
                            color: '#ff4d4f',
                            border: '1px solid #ff4d4f',
                            borderRadius: '4px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: 500
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff1f0';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'white';
                          }}
                        >
                          Delete
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}

              <div className="d-flex gap-2 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
                <Button 
                  type="submit" 
                  disabled={loading}
                  style={{
                    backgroundColor: '#00579a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#004a85';
                  }}
                  onMouseOut={(e) => {
                    if (!loading) e.currentTarget.style.backgroundColor = '#00579a';
                  }}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update Vacancy' : 'Post Vacancy')}
                </Button>
                <Button 
                  onClick={() => navigate('/vacancies')}
                  style={{
                    backgroundColor: 'white',
                    color: '#595959',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    padding: '10px 24px',
                    fontWeight: 500,
                    fontSize: '14px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#00579a';
                    e.currentTarget.style.color = '#00579a';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.color = '#595959';
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#262626',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px',
  transition: 'all 0.2s ease'
};

export default VacancyForm;